import React, { useEffect, useState } from "react";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";

export default function AssetDetailDrawer({ isOpen, onClose, machineryId, onRefreshList, onEdit, onTransfer }) {
  const [activeTab, setActiveTab] = useState("specs");
  const [loading, setLoading] = useState(false);
  const [machineData, setMachineData] = useState(null);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [newDocType, setNewDocType] = useState("RC Book / Registration");
  const [newDocExpiry, setNewDocExpiry] = useState("");
  const [newDocFile, setNewDocFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    if (isOpen && machineryId) {
      fetchMachineDetails();
    }
  }, [isOpen, machineryId]);

  const fetchMachineDetails = async () => {
    setLoading(true);
    try {
      const res = await callApi(`${apiUrl.get_machinery_detail}?machineryId=${machineryId}`, {
        method: "GET",
      });
      if (res?.status === true) {
        setMachineData(res?.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!newDocFile || !machineryId) return;
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append("machineryId", machineryId);
      formData.append("documentType", newDocType);
      formData.append("type", newDocType);
      formData.append("name", newDocType);
      if (newDocExpiry) formData.append("expiryDate", newDocExpiry);
      formData.append(newDocFile.name, newDocFile);

      const res = await callApi(apiUrl.add_machinery_document, {
        method: "POST",
        form_data: formData,
      });

      if (res?.status === true) {
        setIsUploadingDoc(false);
        setNewDocFile(null);
        setNewDocExpiry("");
        fetchMachineDetails();
        onRefreshList?.();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteDoc = async (documentId) => {
    if (!documentId) return;
    try {
      const res = await callApi(`${apiUrl.delete_machinery_document}?documentId=${documentId}`, {
        method: "GET",
      });
      if (res?.status === true) {
        fetchMachineDetails();
        onRefreshList?.();
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="asset-drawer-backdrop" onClick={onClose}>
      <div className="asset-drawer-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="drawer-header">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <span style={{ fontSize: "20px" }}>🚜</span>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#ffffff" }}>
                {safeText(machineData?.name, "Equipment Details")}
              </h3>
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8" }}>
              Asset #{safeText(machineData?.assetNo, "N/A")} • Reg: {safeText(machineData?.machineNumber, "N/A")}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {machineData && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onTransfer?.(machineData);
                  }}
                  style={{
                    background: "rgba(59, 130, 246, 0.2)",
                    border: "1px solid rgba(59, 130, 246, 0.4)",
                    color: "#93c5fd",
                    padding: "0 11px",
                    height: 30,
                    borderRadius: 6,
                    fontSize: "12.5px",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  🔁 Transfer
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onEdit?.(machineData);
                  }}
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#fff",
                    padding: "0 11px",
                    height: 30,
                    borderRadius: 6,
                    fontSize: "12.5px",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  ✏️ Edit
                </button>
              </>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "none",
                color: "#fff",
                width: 30,
                height: 30,
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "0 1.5rem" }}>
          {[
            { id: "specs", label: "Overview & Specs" },
            { id: "docs", label: `Documents (${machineData?.Documents?.length || 0})` },
            { id: "services", label: `Service History (${machineData?.MachineryServices?.length || 0})` },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "11px 16px",
                border: "none",
                background: "transparent",
                fontSize: "13.5px",
                fontWeight: 600,
                color: activeTab === tab.id ? "#2563eb" : "#64748b",
                borderBottom: activeTab === tab.id ? "2px solid #2563eb" : "2px solid transparent",
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Body */}
        <div className="drawer-content">
          {loading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "#64748b", fontSize: "14px", fontWeight: 600 }}>
              Loading equipment details...
            </div>
          ) : (
            <>
              {activeTab === "specs" && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.5rem" }}>
                    <div style={{ background: "#f8fafc", padding: "0.95rem 1.1rem", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                      <span style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}>
                        Current Site
                      </span>
                      <p style={{ margin: "4px 0 0 0", fontWeight: 700, fontSize: "16px", color: "#0f172a" }}>
                        {safeText(machineData?.Site?.name || machineData?.Site, "Unassigned")}
                      </p>
                    </div>
                    <div style={{ background: "#f8fafc", padding: "0.95rem 1.1rem", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                      <span style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}>
                        Operational Status
                      </span>
                      <p style={{ margin: "4px 0 0 0", fontWeight: 700, fontSize: "16px", color: machineData?.status === "Breakdown" ? "#dc2626" : "#059669" }}>
                        ● {safeText(machineData?.status, "Operational")}
                      </p>
                    </div>
                  </div>

                  <h5 style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", marginBottom: "0.85rem", borderBottom: "1px solid #f1f5f9", paddingBottom: "0.5rem" }}>
                    Technical Specifications
                  </h5>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem 1.25rem", marginBottom: "1.5rem" }}>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Make & Model</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px" }}>{safeText(machineData?.make, "")} {safeText(machineData?.model, "—")}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Manufacturing Year</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px" }}>{safeText(machineData?.year, "—")}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Chassis Number</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px", fontFamily: "monospace" }}>{safeText(machineData?.chesisNumber, "—")}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Engine Number</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px", fontFamily: "monospace" }}>{safeText(machineData?.engineNumber, "—")}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Fuel Type</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px" }}>{safeText(machineData?.fuelType, "—")}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Reading Mode</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px" }}>{safeText(machineData?.readingType, "Hours")}</strong>
                    </div>
                  </div>

                  <h5 style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", marginBottom: "0.85rem", borderBottom: "1px solid #f1f5f9", paddingBottom: "0.5rem" }}>
                    Purchase & Financials
                  </h5>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem 1.25rem" }}>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Purchase Cost</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px" }}>₹{Number(machineData?.purchaseCost || 0).toLocaleString()}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Purchase Date</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px" }}>{machineData?.purchaseDate ? moment(machineData.purchaseDate).format("DD MMM YYYY") : "—"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>PO Number</span>
                      <strong style={{ color: "#2563eb", fontSize: "14px" }}>{safeText(machineData?.poNumber, "—")}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>PT Number</span>
                      <strong style={{ color: "#2563eb", fontSize: "14px" }}>{safeText(machineData?.ptNumber, "—")}</strong>
                    </div>
                    <div>
                      <span style={{ color: "#64748b", display: "block", fontSize: "12px", marginBottom: "2px", fontWeight: 500 }}>Vendor</span>
                      <strong style={{ color: "#0f172a", fontSize: "14px" }}>{safeText(machineData?.Vendor?.firmName || machineData?.Vendor?.name || machineData?.Vendor, "—")}</strong>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "docs" && (
                <div>
                  {/* Upload New Document Card */}
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #cbd5e1",
                      borderRadius: 10,
                      padding: "1rem 1.25rem",
                      marginBottom: "1.25rem",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>
                        📤 Upload Machinery Compliance Document / Photo
                      </strong>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => setIsUploadingDoc(!isUploadingDoc)}
                        style={{ fontSize: "12.5px", padding: "4px 11px", borderRadius: 6, fontWeight: 600 }}
                      >
                        {isUploadingDoc ? "Cancel" : "+ Upload New Document"}
                      </button>
                    </div>

                    {isUploadingDoc && (
                      <div className="mt-3 pt-3" style={{ borderTop: "1px dashed #cbd5e1" }}>
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="form-label" style={{ fontSize: "12px", fontWeight: 600 }}>
                              Document Type *
                            </label>
                            <select
                              className="form-select form-select-sm"
                              value={newDocType}
                              onChange={(e) => setNewDocType(e.target.value)}
                              style={{ fontSize: "12.5px" }}
                            >
                              <option value="RC Book / Registration">RC Book / Registration</option>
                              <option value="Insurance Policy">Insurance Policy</option>
                              <option value="Fitness Certificate">Fitness Certificate</option>
                              <option value="PUC Certificate">PUC Certificate</option>
                              <option value="Road Tax Receipt">Road Tax Receipt</option>
                              <option value="National / State Permit">National / State Permit</option>
                              <option value="Purchase Invoice">Purchase Invoice</option>
                              <option value="Maintenance / AMC Contract">Maintenance / AMC Contract</option>
                              <option value="Machine Photo">Machine Photo</option>
                              <option value="Other Regulatory Doc">Other Regulatory Doc</option>
                            </select>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label" style={{ fontSize: "12px", fontWeight: 600 }}>
                              Expiry Date (if applicable)
                            </label>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={newDocExpiry}
                              onChange={(e) => setNewDocExpiry(e.target.value)}
                              style={{ fontSize: "12.5px" }}
                            />
                          </div>

                          <div className="col-md-12">
                            <label className="form-label" style={{ fontSize: "12px", fontWeight: 600 }}>
                              Select File (PDF, PNG, JPG) *
                            </label>
                            <input
                              type="file"
                              className="form-control form-control-sm"
                              accept=".pdf,.png,.jpg,.jpeg"
                              onChange={(e) => setNewDocFile(e.target.files[0] || null)}
                              style={{ fontSize: "12.5px" }}
                            />
                          </div>

                          <div className="col-md-12 text-end mt-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              disabled={!newDocFile || uploadLoading}
                              onClick={handleUploadDocument}
                              style={{ fontWeight: 600, padding: "6px 16px", borderRadius: 6, fontSize: "13px" }}
                            >
                              {uploadLoading ? "Uploading..." : "✓ Upload & Save Document"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {(!Array.isArray(machineData?.Documents) || machineData.Documents.length === 0) ? (
                    <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontSize: "13px" }}>
                      No documents uploaded for this machine yet. Use the upload button above to add RC, Insurance, or PUC papers.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                      {machineData.Documents.map((doc) => {
                        const isExpired = doc?.expiryDate && moment(doc.expiryDate).isBefore(moment());
                        return (
                          <div
                            key={doc?.id || Math.random()}
                            style={{
                              padding: "0.85rem 1rem",
                              border: "1px solid #e2e8f0",
                              borderRadius: 8,
                              background: isExpired ? "#fff1f2" : "#ffffff",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <p style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: "13.5px" }}>
                                📄 {doc?.type || doc?.name}
                              </p>
                              <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: isExpired ? "#dc2626" : "#64748b" }}>
                                {doc?.expiryDate ? `Expiry: ${moment(doc.expiryDate).format("DD MMM YYYY")} (${isExpired ? "Expired" : "Valid"})` : "No expiry date"}
                              </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {doc?.url && (
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    padding: "5px 11px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    background: "#0f172a",
                                    color: "#fff",
                                    borderRadius: 6,
                                    textDecoration: "none",
                                  }}
                                >
                                  View File ↗
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteDoc(doc.id)}
                                className="btn btn-sm btn-outline-danger"
                                style={{ padding: "4px 8px", fontSize: "12px", borderRadius: 6 }}
                                title="Delete Document"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "services" && (
                <div>
                  {(!Array.isArray(machineData?.MachineryServices) || machineData.MachineryServices.length === 0) ? (
                    <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontSize: "13px" }}>
                      No service records logged yet.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                      {machineData.MachineryServices.map((srv) => (
                        <div
                          key={srv.id}
                          style={{
                            padding: "0.85rem 1rem",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            background: "#ffffff",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                            <strong style={{ color: "#0f172a", fontSize: "13.5px" }}>🔧 {srv.serviceType}</strong>
                            <span style={{ fontSize: "12px", color: "#64748b" }}>
                              {moment(srv.serviceDate).format("DD MMM YYYY")}
                            </span>
                          </div>
                          {srv.cost && (
                            <p style={{ margin: "2px 0", fontSize: "12.5px", color: "#059669", fontWeight: 600 }}>
                              Cost: ₹{Number(srv.cost).toLocaleString()}
                            </p>
                          )}
                          {srv.partsReplaced && (
                            <p style={{ margin: "2px 0", fontSize: "12px", color: "#475569" }}>
                              Parts: {srv.partsReplaced}
                            </p>
                          )}
                          {srv.remarks && (
                            <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#64748b", fontStyle: "italic" }}>
                              "{srv.remarks}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
